/**
 * Хук собирает `ChatPageContext` для Canvas-view: канвас и нарратив из
 * `location.hash`, открытая в EntityCard drawer'е сущность из ui-store.
 *
 * Якорь имеет форму `aspect-<scope>-<aspect-id-with-hyphens>`, где
 * scope — `project` или `canvas-<slug>`. См. `NarrativeAspectView.anchorId`.
 *
 * Точное имя канваса вытаскиваем из snapshot'а: NarrativeAspectView slug'ит
 * имя для якоря, обратное декодирование не однозначно. Сравниваем slug'и.
 */

import { useEffect, useState } from 'react';
import type { CanvasView, ChatPageContext } from '@pflow/shared/v2';
import { useUiStore } from '../stores/ui-store';

function slug(s: string): string {
    return s.toLowerCase().replace(/[^a-zа-я0-9]+/gi, '-').replace(/^-|-$/g, '').slice(0, 60);
}

function parseHash(hash: string, canvases: readonly CanvasView[] = []): { canvas?: string; aspectId?: string } {
    if (!hash.startsWith('#aspect-')) return {};
    const tail = hash.slice('#aspect-'.length);
    if (tail.startsWith('project-')) {
        // aspect id (с заменой `.` → `-`) — всё после "project-"
        return { aspectId: tail.slice('project-'.length).replace(/-/g, '.') };
    }
    if (tail.startsWith('canvas-')) {
        // Структура: canvas-<slug>-<aspect-id-with-hyphens>. Slug канваса
        // может содержать дефисы; ищем самый длинный префикс slug'а,
        // совпадающий с одним из канвасов snapshot'а.
        const body = tail.slice('canvas-'.length);
        const cv = canvases
            .map(c => ({ name: c.name, slug: slug(c.name) }))
            .filter(c => body.startsWith(c.slug + '-') || body === c.slug)
            .sort((a, b) => b.slug.length - a.slug.length)[0];
        if (!cv) return {};
        const rest = body === cv.slug ? '' : body.slice(cv.slug.length + 1);
        return {
            canvas:   cv.name,
            aspectId: rest ? rest.replace(/-/g, '.') : undefined,
        };
    }
    return {};
}

export function useCanvasPageContext(canvases: readonly CanvasView[] = []): ChatPageContext {
    const [hash, setHash] = useState<string>(() => (typeof window !== 'undefined' ? window.location.hash : ''));

    useEffect(() => {
        const onChange = (): void => setHash(window.location.hash);
        window.addEventListener('hashchange', onChange);
        return () => window.removeEventListener('hashchange', onChange);
    }, []);

    const openEntity = useUiStore(s => s.cardEntity);
    const { canvas, aspectId } = parseHash(hash, canvases);

    return {
        view: 'canvas',
        canvas,
        aspectId,
        openEntity: openEntity ?? undefined,
    };
}
